import type { WordApiPhrase, WordItem } from './types';

export const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

export const extractErrorMessage = (error: unknown): string | undefined => {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  if (isRecord(error)) {
    const messageCandidate = error.message;
    if (typeof messageCandidate === 'string' && messageCandidate.trim()) {
      return messageCandidate;
    }

    const response = error.response;
    if (isRecord(response)) {
      const data = response.data;
      if (isRecord(data)) {
        const nestedMessage = data.message;
        if (typeof nestedMessage === 'string' && nestedMessage.trim()) {
          return nestedMessage;
        }
      }
    }
  }

  return undefined;
};

const toTranslations = (input: unknown): Record<string, string> | undefined => {
  if (!isRecord(input)) {
    return undefined;
  }

  const entries: Record<string, string> = {};
  for (const [key, value] of Object.entries(input)) {
    if (typeof value === 'string' && value.trim()) {
      entries[key] = value;
    }
  }

  return Object.keys(entries).length ? entries : undefined;
};

const toWordItem = (entry: WordApiPhrase): WordItem | null => {
  if (typeof entry === 'string') {
    const text = entry.trim();
    return text ? { text } : null;
  }

  if (!isRecord(entry)) {
    return null;
  }

  const candidates = ['local', 'phraseLocal', 'text'] as const;
  const textCandidate = candidates
    .map((key) => (typeof entry[key] === 'string' ? String(entry[key]).trim() : ''))
    .find((value) => value.length > 0);

  if (!textCandidate) {
    return null;
  }

  const translations =
    toTranslations(entry.translations) ||
    (typeof entry.fr === 'string' && entry.fr.trim() ? { fr: entry.fr.trim() } : undefined);
  const domain = typeof entry.domain === 'string' ? entry.domain.trim() : '';
  const id = typeof entry.id === 'string' ? entry.id.trim() : '';
  const fileOrder =
    typeof entry.fileOrder === 'number' &&
    Number.isSafeInteger(entry.fileOrder) &&
    entry.fileOrder > 0
      ? entry.fileOrder
      : undefined;

  return {
    text: textCandidate,
    translations,
    ...(domain ? { domain } : {}),
    ...(id ? { id } : {}),
    ...(fileOrder === undefined ? {} : { fileOrder }),
  };
};

const flattenPhrases = (value: unknown): WordApiPhrase[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  const phrases: WordApiPhrase[] = [];
  value.forEach((entry) => {
    if (isRecord(entry) && Array.isArray(entry.phrases)) {
      entry.phrases.forEach((phrase) => {
        if (typeof phrase === 'string' || isRecord(phrase)) {
          phrases.push(phrase);
        }
      });
    } else if (typeof entry === 'string' || isRecord(entry)) {
      phrases.push(entry);
    }
  });
  return phrases;
};

export const normalizeWordItems = (
  raw: unknown,
  pageLimit: number,
): { items: WordItem[]; totalPages?: number } => {
  const items: WordItem[] = [];
  let totalPages: number | undefined;

  const appendItems = (phrases: WordApiPhrase[]) => {
    phrases.forEach((phrase) => {
      const normalized = toWordItem(phrase);
      if (normalized) {
        items.push(normalized);
      }
    });
  };

  if (isRecord(raw)) {
    if (raw.totalPages !== undefined) {
      const total = Number(raw.totalPages);
      if (Number.isFinite(total)) {
        totalPages = total;
      }
    }

    if ('data' in raw) {
      appendItems(flattenPhrases(raw.data));
    } else if (Array.isArray(raw.phrases)) {
      appendItems(flattenPhrases(raw.phrases));
    } else {
      const normalized = toWordItem(raw);
      if (normalized) {
        items.push(normalized);
      }
    }
  } else if (Array.isArray(raw)) {
    appendItems(flattenPhrases(raw));
  }

  return {
    items: items.slice(0, pageLimit),
    totalPages,
  };
};

export const getPreferredTranslation = (translations?: Record<string, string>) => {
  const preferred = translations?.fr;
  const firstNonEmpty = Object.values(translations || {}).find((value) => !!value && String(value).trim().length > 0);
  return (preferred && preferred.trim()) || (firstNonEmpty && String(firstNonEmpty).trim()) || undefined;
};
